import {
    CheckCircleIcon,
    CloudArrowUpIcon,
    TrashIcon,
    VideoCameraIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const BuyerAppSettings = () => {
  const [bannerVideo, setBannerVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBannerSettings();
  }, []);

  const fetchBannerSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/settings/buyer-app`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.bannerVideo) {
        setBannerVideo(res.data.bannerVideo);
      }
    } catch (error) {
      console.error('Failed to fetch banner settings:', error);
      toast.error('Failed to load banner settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error('Video file size must be less than 50MB');
      return;
    }

    setVideoFile(file);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setVideoPreview(previewUrl);
  };

  const handleUpload = async () => {
    if (!videoFile) {
      toast.error('Please select a video file');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Uploading banner video...');

    try {
      const formData = new FormData();
      formData.append('bannerVideo', videoFile);

      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/settings/buyer-app/banner`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            toast.loading(`Uploading... ${percentCompleted}%`, { id: toastId });
          }
        }
      );

      setBannerVideo(res.data.bannerVideo);
      setVideoFile(null);
      setVideoPreview(null);
      toast.success('Banner video uploaded successfully!', { id: toastId });
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload banner video', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the current banner video?')) {
      return;
    }

    const toastId = toast.loading('Deleting banner video...');

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/settings/buyer-app/banner`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBannerVideo(null);
      toast.success('Banner video deleted successfully!', { id: toastId });
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete banner video', { id: toastId });
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const cancelUpload = () => {
    setVideoFile(null);
    setVideoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-gray-900">
          Buyer App Settings
        </h1>
        <p className="text-gray-600 mt-1">
          Manage banner video for the buyer mobile application
        </p>
      </div>

      {/* Current Banner Video */}
      {bannerVideo && !videoPreview && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6 text-green-500" />
              Current Banner Video
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="w-5 h-5" />
              Delete
            </motion.button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Video Player */}
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                src={bannerVideo.url}
                className="w-full h-full object-contain"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                controls
              />
            </div>

            {/* Video Details */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">File Name</label>
                <p className="text-gray-900 mt-1">{bannerVideo.originalName || 'banner-video.mp4'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">File Size</label>
                <p className="text-gray-900 mt-1">
                  {bannerVideo.size ? `${(bannerVideo.size / (1024 * 1024)).toFixed(2)} MB` : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Upload Date</label>
                <p className="text-gray-900 mt-1">
                  {bannerVideo.uploadedAt 
                    ? format(new Date(bannerVideo.uploadedAt), 'MMM dd, yyyy - HH:mm')
                    : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <div className="mt-1">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upload New Video */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CloudArrowUpIcon className="w-6 h-6 text-primary-500" />
          {bannerVideo ? 'Update' : 'Upload'} Banner Video
        </h2>

        {/* Video Preview */}
        {videoPreview && (
          <div className="mb-6">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Preview</label>
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video max-w-2xl">
              <video
                src={videoPreview}
                className="w-full h-full object-contain"
                controls
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
              <span>{videoFile?.name}</span>
              <span>{(videoFile?.size / (1024 * 1024)).toFixed(2)} MB</span>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            className="hidden"
            id="video-upload"
          />

          {!videoFile ? (
            <label
              htmlFor="video-upload"
              className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <VideoCameraIcon className="w-16 h-16 text-gray-400 mb-4" />
                <p className="mb-2 text-sm text-gray-600">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">MP4, MOV, AVI (MAX. 50MB)</p>
              </div>
            </label>
          ) : (
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudArrowUpIcon className="w-5 h-5" />
                {uploading ? 'Uploading...' : 'Upload Video'}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={cancelUpload}
                disabled={uploading}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </motion.button>
            </div>
          )}
        </div>

        {/* Upload Guidelines */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Upload Guidelines</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Recommended resolution: 1920x1080 (Full HD) or higher</li>
            <li>• Maximum file size: 50MB</li>
            <li>• Supported formats: MP4, MOV, AVI, WebM</li>
            <li>• Optimal duration: 5-30 seconds for best user experience</li>
            <li>• Ensure video is horizontally oriented for mobile viewing</li>
          </ul>
        </div>
      </motion.div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-br from-primary-50 to-orange-50 rounded-xl p-6 border border-primary-200"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <VideoCameraIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Banner Video Display</h3>
            <p className="text-gray-700">
              The banner video will be displayed on the home screen of the buyer mobile application.
              It auto-plays when users open the app and serves as an engaging introduction to featured
              car listings and special promotions.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BuyerAppSettings;
